/**
 * @fileoverview Local ScrollArea shim for the vendored Extend UI file-system
 * component.
 *
 * The upstream component targets Extend's Base-UI-based scroll-area, whose
 * API (`orientation`, `viewportRef`, `viewportClassName`, `viewportProps`,
 * `ScrollAreaPrimitive.Content`) differs from Morgan's Radix-based
 * `@/ui/primitives/scroll-area`. Rather than rework every call site, this
 * shim exposes the same prop surface on top of native overflow scrolling.
 */

import * as React from "react";

import { cn } from "@/utils/misc";

type ScrollAreaProps = Omit<
  React.HTMLAttributes<HTMLDivElement>,
  "children"
> & {
  children?: React.ReactNode;
  orientation?: "vertical" | "horizontal" | "both";
  viewportClassName?: string;
  viewportProps?: React.HTMLAttributes<HTMLDivElement>;
  viewportRef?: React.Ref<HTMLDivElement>;
};

/**
 * Single-element scroll container: the root and the viewport are the same
 * div, so frame classes (`className`) and viewport classes
 * (`viewportClassName`) merge onto one element. Native overlay scrollbars
 * stand in for the custom-drawn ones upstream.
 */
export function ScrollArea({
  className,
  children,
  orientation = "both",
  viewportClassName,
  viewportProps,
  viewportRef,
  ...props
}: ScrollAreaProps): React.ReactElement {
  const { className: viewportPropsClassName, ...restViewportProps } =
    viewportProps ?? {};

  return (
    <div
      {...props}
      {...restViewportProps}
      ref={viewportRef}
      className={cn(
        "relative size-full min-h-0",
        orientation === "vertical" && "overflow-y-auto overflow-x-hidden",
        orientation === "horizontal" && "overflow-x-auto overflow-y-hidden",
        orientation === "both" && "overflow-auto",
        className,
        viewportPropsClassName,
        viewportClassName,
      )}
    >
      {children}
    </div>
  );
}

const ScrollAreaContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function ScrollAreaContent({ className, ...props }, ref) {
  return <div ref={ref} className={cn(className)} {...props} />;
});

/**
 * Minimal stand-in for the Base UI scroll-area primitive namespace; only the
 * `Content` part is used by the vendored file-system component.
 */
// eslint-disable-next-line react-refresh/only-export-components -- namespace object mirrors the upstream `ScrollAreaPrimitive` import shape
export const ScrollAreaPrimitive = {
  Content: ScrollAreaContent,
};
